import { Injectable } from '@angular/core';
import { filter, firstValueFrom, Observable, of, ReplaySubject, throwError } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { Apollo, QueryRef } from 'apollo-angular';
import {
  Attribute,
  FatModuleVariant,
  Id,
  ModuleBlueprint,
  ModulePlacement,
  Scope,
  SpacialPosition,
} from '@ess/jg-rule-executor';
import { MUTATIONS, QUERIES } from '@src/app/services/apollo/apollo.queries';
import { parseRoles, Role, User } from '@src/app/model/userdata';
import { Retailer } from '@src/app/model/retailer';
import { PortalConfiguration } from '@src/app/model/portal-configuration';
import { PortalModuleBlueprint } from '@src/app/model/portal-module-blueprint';
import { PortalCatalog } from '@src/app/model/portal-catalog';
import { PortalEventTemplate } from '@src/app/model/portal-event-template';
import { PortalInvariantTemplate } from '@src/app/model/portal-invariant-template';
import { GetCurrentUser, GetCurrentUser_currentUser } from '@src/app/generated/GetCurrentUser';
import { PasswordForgotten } from '@src/app/generated/PasswordForgotten';
import { GetAllUsers } from '@src/app/generated/GetAllUsers';
import { GetConfigurationByCode } from '@src/app/generated/GetConfigurationByCode';
import { ResetMfaMutation } from '@src/app/generated/ResetMfaMutation';
import { GetAllRetailers } from '@src/app/generated/GetAllRetailers';
import { UploadVariants } from '@src/app/generated/UploadVariants';
import { UploadUser } from '@src/app/generated/UploadUser';
import { AddBlueprint } from '@src/app/generated/AddBlueprint';
import { UpdateBlueprint } from '@src/app/generated/UpdateBlueprint';
import { CopyBlueprint } from '@src/app/generated/CopyBlueprint';
import { BlockUser } from '@src/app/generated/BlockUser';
import { GetRoles } from '@src/app/generated/GetRoles';
import { SetRoles } from '@src/app/generated/SetRoles';
import { GetModuleEventTemplates } from '@src/app/generated/GetModuleEventTemplates';
import { GetModuleInvariantTemplates } from '@src/app/generated/GetModuleInvariantTemplates';
import { GetEnvInvariantTemplates } from '@src/app/generated/GetEnvInvariantTemplates';
import { NewEventTemplate } from '@src/app/generated/NewEventTemplate';
import { UpdateEventTemplate } from '@src/app/generated/UpdateEventTemplate';
import { NewModuleInvariantTemplate } from '@src/app/generated/NewModuleInvariantTemplate';
import { UpdateModuleInvariantTemplate } from '@src/app/generated/UpdateModuleInvariantTemplate';
import { NewEnvInvariantTemplate } from '@src/app/generated/NewEnvInvariantTemplate';
import { UpdateEnvInvariantTemplate } from '@src/app/generated/UpdateEnvInvariantTemplate';
import { UpdateConfiguration } from '@src/app/generated/UpdateConfiguration';
import { DeleteBlueprint } from '@src/app/generated/DeleteBlueprint';
import { ArchiveBlueprint } from '@src/app/generated/ArchiveBlueprint';
import { PublishBlueprint } from '@src/app/generated/PublishBlueprint';
import { UpdateCatalogMutation } from '@src/app/generated/UpdateCatalogMutation';
import { GetCatalog } from '@src/app/generated/GetCatalog';
import { AddRetailer } from '@src/app/generated/AddRetailer';
import { UpdateRetailer } from '@src/app/generated/UpdateRetailer';
import { GetCurrentRetailer } from '@src/app/generated/GetCurrentRetailer';
import { GetAllAttributes } from '@src/app/generated/GetAllAttributes';
import { GetAttributeById } from '@src/app/generated/GetAttributeById';
import { PortalAttribute } from '@src/app/model/portal-attribute';
import { UpdateAttribute } from '@src/app/generated/UpdateAttribute';
import { NewAttribute } from '@src/app/generated/NewAttribute';
import { GetAllPublicRetailers } from '@src/app/generated/GetAllPublicRetailers';
import { PublicRetailer } from '@src/app/model/public-retailer';
import { PortalModuleCategory } from '@src/app/model/portal-module-category';
import { UpsertModuleCategoryMutation } from '@src/app/generated/UpsertModuleCategoryMutation';
import { SetAppRetailerTranslations } from '@src/app/generated/SetAppRetailerTranslations';
import { SetAppDefaultTranslations } from '@src/app/generated/SetAppDefaultTranslations';
import { SetInvariantRetailerTranslations } from '@src/app/generated/SetInvariantRetailerTranslations';
import { GetDefaultTranslations } from '@src/app/generated/GetDefaultTranslations';
import { SetAttributeRetailerTranslations } from '@src/app/generated/SetAttributeRetailerTranslations';
import { SetScopes } from '@src/app/generated/SetScopes';
import { GetAllMaterials } from '@src/app/generated/GetAllMaterials';
import { PortalModuleMaterial } from '@src/app/model/portal-module-material';
import { NewMaterial } from '@src/app/generated/NewMaterial';
import { DeleteMaterial } from '@src/app/generated/DeleteMaterial';
import { UpdateMaterial } from '@src/app/generated/UpdateMaterial';
import { PortalModuleTexture } from '@src/app/model/portal-module-texture';
import { DeleteTexture } from '@src/app/generated/DeleteTexture';
import { NewTexture } from '@src/app/generated/NewTexture';
import { GetAllTextures } from '@src/app/generated/GetAllTextures';
import { UpdateTexture } from '@src/app/generated/UpdateTexture';
import { NewSinglePriceStocks } from '@src/app/generated/NewSinglePriceStocks';
import { NewWoodPriceStocks } from '@src/app/generated/NewWoodPriceStocks';
import { GetAllModuleCategorySettings } from '@src/app/generated/GetAllModuleCategorySettings';
import { PortalModuleVariant } from '@src/app/model/portal-module-variant';
import { MakeConfigWithVariant } from '@src/app/generated/MakeConfigWithVariant';
import { GenerateManualPayload } from '@src/app/generated/GenerateManualPayload';
import { GetBlueprintsByName } from '@src/app/generated/GetBlueprintsByName';
import { GetCurrentModuleBlueprints } from '@src/app/generated/GetCurrentModuleBlueprints';
import { GetArchivedModuleBlueprints } from '@src/app/generated/GetArchivedModuleBlueprints';
import { GetVariantsByBlueprintId } from '@src/app/generated/GetVariantsByBlueprintId';
import { GetRetailerApiKey } from '@src/app/generated/GetRetailerApiKey';
import { PortalManualConfiguration } from '@src/app/model/portal-manual-configuration';
import { uploadEANsForRetailer } from '@src/app/generated/uploadEANsForRetailer';
import { GetFreeEans } from '@src/app/generated/GetFreeEans';
import { PortalConfigurationPlacement } from '@src/app/model/portal-configuration-placement';
import { PlacementInformation } from '@src/app/model/placement-information';
import { GetAllFatVariants, GetAllFatVariants_allBlueprints } from '@src/app/generated/GetAllFatVariants';
import { RequestSort, SubjectType, WoodType } from '@src/app/generated/globalTypes';
import { flatten } from 'lodash';
import { SetVariantRetailerTranslations } from '@src/app/generated/SetVariantRetailerTranslations';
import { GetFilteredTranslations } from '@src/app/generated/GetFilteredTranslations';
import { HttpHeaders } from '@angular/common/http';
import { GetAllFatVariantsWithKey } from '@src/app/generated/GetAllFatVariantsWithKey';
import { GenerateManualPayloadWithKey } from '@src/app/generated/GenerateManualPayloadWithKey';
import { VariantTranslations } from '@src/app/model/variant-translations';
import { GetVariantTranslations } from '@src/app/generated/GetVariantTranslations';
import { GetTranslationUpdatingStatus } from '@src/app/generated/GetTranslationUpdatingStatus';
import { Documentation } from '@src/app/model/documentation';
import { GetDocumentationBySubjectType } from '@src/app/generated/GetDocumentationBySubjectType';
import { updateDocumentation } from '@src/app/generated/updateDocumentation';
import { CopyRetailer } from '@src/app/generated/CopyRetailer';
import { GetFatVariantsByVariantIds } from '@src/app/generated/GetFatVariantsByVariantIds';
import { GetFatVariantsByVariantIdsWithKey } from '@src/app/generated/GetFatVariantsByVariantIdsWithKey';
import { ApolloClient } from '@apollo/client';

@Injectable({
  providedIn: 'root',
})
export class ApolloService {
  private _getAllRetailersRef: QueryRef<GetAllRetailers> = undefined;
  private _getAllPublicRetailersRef: QueryRef<GetAllPublicRetailers> = undefined;
  private _getCurrentBlueprintsRef: QueryRef<GetCurrentModuleBlueprints> = undefined;
  private _getArchivedBlueprintsRef: QueryRef<GetArchivedModuleBlueprints> = undefined;
  private _getAllUsersRef: QueryRef<GetAllUsers> = undefined;
  private _getEventTemplateRef: QueryRef<GetModuleEventTemplates> = undefined;
  private _getModuleInvariantTemplateRef: QueryRef<GetModuleInvariantTemplates> = undefined;
  private _getEnvModuleInvariantTemplateRef: QueryRef<GetEnvInvariantTemplates> = undefined;
  private _getCatalogRef: QueryRef<GetCatalog> = undefined;
  private _getCurrentRetailerRef: QueryRef<GetCurrentRetailer> = undefined;
  private _getAllModuleCategoriesRef: QueryRef<GetAllModuleCategorySettings> = undefined;
  private _getAllAttributesRef: QueryRef<GetAllAttributes> = undefined;
  private _getTranslationsRef: QueryRef<GetFilteredTranslations> = undefined;
  private _getDefaultTranslationsRef: QueryRef<GetDefaultTranslations> = undefined;
  private _getVariantTranslationsRef: QueryRef<GetVariantTranslations> = undefined;
  private _getAllMaterialsRef: QueryRef<GetAllMaterials> = undefined;
  private _getAllTexturesRef: QueryRef<GetAllTextures> = undefined;

  private _currentBlueprintsObs: ReplaySubject<{ blueprints: PortalModuleBlueprint[] }>;
  private _archivedBlueprintsObs: ReplaySubject<{ blueprints: PortalModuleBlueprint[] }>;

  public statusFilter: string = null; // only used for refreshing blueprints

  constructor(
    private _apollo: Apollo,
  ) {
  }

  /**
   * Queries
   */
  getCurrentUser$(): Observable<User> {
    return this._apollo.query<GetCurrentUser>({
      query: QUERIES.currentUser,
      fetchPolicy: 'no-cache',
    }).pipe(
      switchMap((r) => {
        if (r.error) {
          return throwError(() => r.error);
        }
        const user: GetCurrentUser_currentUser | null = r.data?.currentUser;
        if (!!user) {
          // The 'never' lastModified ensures this downloaded data is not re-uploaded.
          const newUser = new User(
            user.id,
            user.email,
            user.totpQr,
            parseRoles(user.roles) || [],
            null,
            user.scopes?.map(s => new Scope(s)) || []
          );
          return of(newUser);
        } else {
          return of(null);
        }
      }),
      first()
    );
  }

  // "first" is a shadowed declaration, but using it is consistent.
  getAllUsers$(
    first: number = 10,
    drop: number = 0,
    blocked: boolean = false,
    sort?: RequestSort[]
  ): Observable<{ users: User[], count: number, hasNextPage: boolean }> {
    this._getAllUsersRef = this._apollo.watchQuery<GetAllUsers>({
      query: QUERIES.allUsers,
      variables: {
        blocked,
        first,
        drop,
        sort,
      },
      fetchPolicy: 'no-cache',
    });
    return this._getAllUsersRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.allUsers;
        return {
          users: data.edges?.map(user => new User(
            user.node.id,
            user.node.email,
            null,
            user.node.roles.map(role => new Role(
              role.name, [], [], false
            )),
            user.node.blocked,
            user.node.scopes.map(s => new Scope(s))
          )),
          count: data.count,
          hasNextPage: data?.pageInfo?.hasNextPage,
        };
      })
    );
  }

  getAllRoles$(): Observable<string[]> {
    return this._apollo.query<GetRoles>({
      query: QUERIES.roles,
      fetchPolicy: 'cache-first',
    }).pipe(map(r => r.data.allRoleNames));
  }

  getAllUsersRefresh(): void {
    this._getAllUsersRef.refetch();
  }

  // "first" is a shadowed declaration, but using it is consistent.
  // note: we query the stocks unpaged
  getAllRetailers$(
    first: number = 10,
    drop: number = 0,
    sort?: RequestSort[]
  ): Observable<{ retailers: Retailer[], count: number, hasNextPage: boolean }> {
    this._getAllRetailersRef = this._apollo.watchQuery<GetAllRetailers>({
      query: QUERIES.allRetailers,
      fetchPolicy: 'no-cache',
      variables: {
        first,
        drop,
        sort,
      },
    });
    return this._getAllRetailersRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data
          ? r.data.allRetailers
          : {
            edges: [],
            count: 0,
            pageInfo: {
              hasNextPage: false,
            },
          };
        return {
          retailers: data.edges?.map(retailer =>
            Retailer.fromGql(retailer.node)
          ),
          count: data.count,
          hasNextPage: data?.pageInfo?.hasNextPage,
        };
      })
    );
  }

  getAllRetailersRefresh(): void {
    this._getAllRetailersRef.refetch();
    this._getAllPublicRetailersRef.refetch();
  }

  // "first" is a shadowed declaration, but using it is consistent.
  // note: we query the stocks unpaginated
  getAllPublicRetailers$(): Observable<{ retailers: PublicRetailer[], count: number, hasNextPage: boolean }> {
    if (!this._getAllPublicRetailersRef) {
      this._getAllPublicRetailersRef = this._apollo.watchQuery<GetAllPublicRetailers>({
        query: QUERIES.allPublicRetailers,
        fetchPolicy: 'no-cache',
      });
    }
    return this._getAllPublicRetailersRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data
          ? r.data.allPublicRetailers
          : {
            edges: [],
            count: 0,
            pageInfo: {
              hasNextPage: false,
            },
          };
        return {
          retailers: data.edges?.map(publicRetailer =>
            PublicRetailer.fromGql(publicRetailer.node)
          ),
          count: data.count,
          hasNextPage: data?.pageInfo?.hasNextPage,
        };
      })
    );
  }

  getAllModuleCategories$(archived: boolean = false): Observable<PortalModuleCategory[]> {
    this._getAllModuleCategoriesRef = this._apollo.watchQuery<GetAllModuleCategorySettings>({
      query: QUERIES.allModuleCategorySettings,
      fetchPolicy: 'no-cache',
      variables: { archived },
    });
    return this._getAllModuleCategoriesRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map(r => r.data ? r.data.allModuleCategorySettings?.map(mc => PortalModuleCategory.fromGql(mc)) : [])
    );
  }

  getAllModuleCategoriesRefresh(): void {
    this._getAllModuleCategoriesRef.refetch();
  }

  getCurrentRetailer$(): Observable<Retailer> {
    this._getCurrentRetailerRef = this._apollo.watchQuery<GetCurrentRetailer>({
      query: QUERIES.currentRetailer,
      fetchPolicy: 'no-cache',
    });

    return this._getCurrentRetailerRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.currentRetailer;
        return Retailer.fromGql(data);
      }),
    );
  }

  getCurrentRetailerRefresh(): void {
    this._getCurrentRetailerRef.refetch();
  }

  getCatalog$(): Observable<PortalCatalog> {
    this._getCatalogRef = this._apollo.watchQuery<GetCatalog>({
      query: QUERIES.catalog,
      fetchPolicy: 'no-cache',
    });
    return this._getCatalogRef.valueChanges.pipe(
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.catalogSetting;
        return data ? PortalCatalog.fromGql(data) : PortalCatalog.default();
      })
    );
  }

  getCatalogRefresh(): void {
    this._getCatalogRef.refetch();
  }

  async getAllFatVariantsByWoodtype$(woodtype: WoodType): Promise<FatModuleVariant[]> {
    const result = (await firstValueFrom(
      this._apollo.query<GetAllFatVariants>({
        query: QUERIES.allBlueprintsWithVariants,
        variables: {},
        fetchPolicy: 'no-cache',
      })
    )).data?.allBlueprints;
    return this._flattenFatModuleVariants(result, woodtype);
  }

  /**
   * This function is called when getting the manual payload without being logged in.
   * @param woodtype
   * @param retailerKey will be checked in the backend to maintain security
   */
  async getAllFatVariantsByWoodtypeWithKey$(woodtype: WoodType, retailerKey: string): Promise<FatModuleVariant[]> {
    const result = (await firstValueFrom(
      this._apollo.query<GetAllFatVariantsWithKey>({
        query: QUERIES.allBlueprintsWithVariantsWithKey,
        variables: {},
        fetchPolicy: 'no-cache',
        context: {
          headers: new HttpHeaders({
            'X-API-KEY': retailerKey,
          }),
        },
      })
    )).data?.allBlueprintsWithKey;
    return this._flattenFatModuleVariants(result, woodtype);
  }

  async getFatVariantsByVariantIds$(
    woodType: WoodType,
    variantIds: Id<ModuleBlueprint>[]
  ): Promise<FatModuleVariant[]> {
    if (variantIds.length === 0) {
      return [];
    }
    const result = (await firstValueFrom(
      this._apollo.query<GetFatVariantsByVariantIds>({
        query: QUERIES.fatVariantsByVariantIds,
        variables: { woodType, variantIds },
        fetchPolicy: 'no-cache',
      })
    )).data?.fatVariantsByVariantIds;
    return this._flattenFatModuleVariants(result, woodType);
  }

  async getFatVariantsByVariantIdsWithKey$(
    woodType: WoodType,
    variantIds: Id<ModuleBlueprint>[],
    retailerKey: string
  ): Promise<FatModuleVariant[]> {
    if (variantIds.length === 0) {
      return [];
    }
    const result = (await firstValueFrom(
      this._apollo.query<GetFatVariantsByVariantIdsWithKey>({
        query: QUERIES.fatVariantsByVariantIdsWithKey,
        variables: { woodType, variantIds },
        fetchPolicy: 'no-cache',
        context: {
          headers: new HttpHeaders({
            'X-API-KEY': retailerKey,
          }),
        },
      })
    )).data?.fatVariantsByVariantIdsWithKey;
    return this._flattenFatModuleVariants(result, woodType);
  }

  private _flattenFatModuleVariants(result: GetAllFatVariants_allBlueprints[], woodtype: WoodType): FatModuleVariant[] {
    const allFatVars = result?.map((bp) => {
      const pmb = PortalModuleBlueprint.fromGql(bp);
      const fatVarsForBp = bp.variantsPerWoodType.values
        .filter(v => v.key === woodtype)
        .map(v => v.value.map(
          variant => new FatModuleVariant(PortalModuleVariant.fromGql(variant), pmb)
        ));
      return flatten<FatModuleVariant>(fatVarsForBp);
    });
    return flatten<FatModuleVariant>(allFatVars);
  }

  // This function retrieves all draft and published blueprints
  getCurrentBlueprints$(): Observable<{ blueprints: PortalModuleBlueprint[] }> {
    if (!this._currentBlueprintsObs) {
      this._currentBlueprintsObs = new ReplaySubject<{ blueprints: PortalModuleBlueprint[] }>();
      this._getCurrentBlueprintsRef = this._apollo.watchQuery<GetCurrentModuleBlueprints>({
        query: QUERIES.allCurrentBlueprints,
        variables: {},
        fetchPolicy: 'no-cache',
      });
      this._getCurrentBlueprintsRef.valueChanges.pipe(
        // Exclude partial but non-error results.
        filter(result => result.dataState === 'complete'),
        map((r) => {
          const data = r.data?.allCurrentBlueprints;
          return {
            blueprints: data?.map(blueprint => PortalModuleBlueprint.fromGql(blueprint)),
          };
        })
      ).subscribe(this._currentBlueprintsObs);
    }
    return this._currentBlueprintsObs.asObservable();
  }

  // This function retrieves all archived blueprints
  getArchivedBlueprints$(): Observable<{ blueprints: PortalModuleBlueprint[] }> {
    if (!this._archivedBlueprintsObs) {
      this._archivedBlueprintsObs = new ReplaySubject<{ blueprints: PortalModuleBlueprint[] }>();
      this._getArchivedBlueprintsRef = this._apollo.watchQuery<GetArchivedModuleBlueprints>({
        query: QUERIES.allArchivedBlueprints,
        variables: {},
        fetchPolicy: 'no-cache',
      });
      this._getArchivedBlueprintsRef.valueChanges.pipe(
        // Exclude partial but non-error results.
        filter(result => result.dataState === 'complete'),
        map((r) => {
          const data = r.data?.allBlueprintsArchived;
          return {
            blueprints: data?.map(blueprint => PortalModuleBlueprint.fromGql(blueprint)),
          };
        })
      ).subscribe(this._archivedBlueprintsObs);
    }
    return this._archivedBlueprintsObs.asObservable();
  }

  getBlueprintsByName$(name: string): Observable<PortalModuleBlueprint[]> {
    return this._apollo.watchQuery<GetBlueprintsByName>({
      query: QUERIES.blueprintsByName,
      variables: { name },
      fetchPolicy: 'no-cache',
    }).valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.blueprintsByName;
        return data?.map(blueprint => PortalModuleBlueprint.fromGql(blueprint));
      })
    );
  }

  getBlueprintsRefresh(): void {
    if (this.statusFilter === 'Archived') {
      this.getArchivedBlueprintsRefresh();
    } else {
      this.getCurrentBlueprintsRefresh();
    }
  }

  getVariantTranslationsRefresh(): void {
    this._getVariantTranslationsRef.refetch();
  }

  getCurrentBlueprintsRefresh(): void {
    // this._getCurrentBlueprintsRef.resetLastResults(); // TODO: Was removed, do I need anything to replace it?
    this._getCurrentBlueprintsRef.refetch();
  }

  getArchivedBlueprintsRefresh(): void {
    // this._getArchivedBlueprintsRef.resetLastResults(); // TODO: Was removed, do I need anything to replace it?
    this._getArchivedBlueprintsRef.refetch();
  }

  getAllEventTemplates$(): Observable<{ eventTemplates: PortalEventTemplate[] }> {
    this._getEventTemplateRef = this._apollo.watchQuery<GetModuleEventTemplates>({
      query: QUERIES.moduleEventTemplates,
      variables: {},
      fetchPolicy: 'no-cache',
    });
    return this._getEventTemplateRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.moduleEventTemplates;
        return {
          eventTemplates: data?.map(eventTemplate => PortalEventTemplate.fromGql(eventTemplate)),
        };
      })
    );
  }

  getAllEventTemplatesRefresh(): void {
    this._getEventTemplateRef.refetch();
  }

  getAllModuleInvariantTemplates$(): Observable<{ invariantTemplates: PortalInvariantTemplate[] }> {
    this._getModuleInvariantTemplateRef = this._apollo.watchQuery<GetModuleInvariantTemplates>({
      query: QUERIES.moduleInvariantTemplates,
      variables: {},
      fetchPolicy: 'no-cache',
    });
    return this._getModuleInvariantTemplateRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.moduleInvariantTemplates;
        return {
          invariantTemplates: data?.map(invariantTemplate => PortalInvariantTemplate.fromGql(invariantTemplate)),
        };
      })
    );
  }

  getAllModuleInvariantTemplatesRefresh(): void {
    this._getModuleInvariantTemplateRef.refetch();
  }

  getVariantsByBlueprintId(id: Id<ModuleBlueprint>): Observable<PortalModuleVariant[]> {
    return this._apollo.query<GetVariantsByBlueprintId>({
      query: QUERIES.variantsByBlueprintId,
      variables: { id },
      fetchPolicy: 'no-cache',
    }).pipe(switchMap((r) => {
      if (r.error) {
        return throwError(() => r.error);
      }
      const variants = r.data?.variantsByBlueprintId;
      if (!!variants) {
        return of(variants.map(v => PortalModuleVariant.fromGql(v)));
      } else {
        return of(null);
      }
    }));
  }

  getAllEnvInvariantTemplates$(): Observable<{ invariantTemplates: PortalInvariantTemplate[] }> {
    this._getEnvModuleInvariantTemplateRef = this._apollo.watchQuery<GetEnvInvariantTemplates>({
      query: QUERIES.envInvariantTemplates,
      variables: {},
      fetchPolicy: 'no-cache',
    });
    return this._getEnvModuleInvariantTemplateRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.envInvariantTemplates;
        return {
          invariantTemplates: data?.map(invariantTemplate => PortalInvariantTemplate.fromGql(invariantTemplate)),
        };
      })
    );
  }

  getAllEnvInvariantTemplatesRefresh(): void {
    this._getEnvModuleInvariantTemplateRef.refetch();
  }

  getConfigurationByCode$(code: string): Observable<PortalConfiguration> {
    return this._apollo.query<GetConfigurationByCode>({
      query: QUERIES.configuration,
      variables: { code },
      fetchPolicy: 'no-cache',
    }).pipe(switchMap((r) => {
      if (r.error) {
        return throwError(() => r.error);
      }
      const config = r.data?.configuration;
      if (!!config) {
        return of(new PortalConfiguration(
          config.id,
          config.name,
          config.email,
          config.code,
          PortalConfigurationPlacement.fromGql(config.configurationPlacement),
          config.isPreConfiguration
        ));
      } else {
        return of(null);
      }
    }));
  }

  // "first" is a shadowed declaration, but using it is consistent.
  // note: we query the stocks unpaged
  getAllAttributes$(
    first: number = 10,
    drop: number = 0,
    sort?: RequestSort[]
  ): Observable<{ attributes: PortalAttribute[], count: number, hasNextPage: boolean }> {
    this._getAllAttributesRef = this._apollo.watchQuery<GetAllAttributes>({
      query: QUERIES.allAttributes,
      fetchPolicy: 'no-cache',
      variables: {
        first,
        drop,
        sort,
      },
    });
    return this._getAllAttributesRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data?.allAttributes;
        return {
          attributes: data.edges?.map(attribute => PortalAttribute.fromGql(attribute.node)),
          count: data.count,
          hasNextPage: data?.pageInfo?.hasNextPage,
        };
      })
    );
  }

  getAllAttributesRefresh(): void {
    this._getAllAttributesRef.refetch();
  }

  getAttributeById$(id: Id<Attribute>): Observable<PortalAttribute> {
    return this._apollo.query<GetAttributeById>({
      query: QUERIES.attribute,
      variables: { id },
      fetchPolicy: 'no-cache',
    }).pipe(switchMap((r) => {
      if (r.error) {
        return throwError(() => r.error);
      }
      const attribute = r.data?.attribute;
      if (!!attribute) {
        return of(new PortalAttribute(
          attribute.id,
          attribute.name,
          attribute.attributeType,
          attribute.title
        ));
      } else {
        return of(null);
      }
    }));
  }

  getTranslations$(): Observable<{ translations: object[] }> { // TODO: Type???
    this._getTranslationsRef = this._apollo.watchQuery<GetFilteredTranslations>({
      query: QUERIES.filteredTranslations,
      variables: {},
      fetchPolicy: 'no-cache',
    });
    return this._getTranslationsRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map(r => ({
        translations: r.data?.filteredTranslations,
      }))
    );
  }

  getDefaultTranslations$(): Observable<{ translations: object[] }> { // TODO: Type???
    this._getDefaultTranslationsRef = this._apollo.watchQuery<GetDefaultTranslations>({
      query: QUERIES.defaultTranslations,
      variables: {},
      fetchPolicy: 'no-cache',
    });
    return this._getDefaultTranslationsRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map(r => ({
        translations: r.data?.defaultTranslations,
      }))
    );
  }

  async getTranslationsRefresh(canGetDefaultTranslations: boolean): Promise<void> {
    await this._getTranslationsRef.refetch();
    if (canGetDefaultTranslations) {
      await this._getDefaultTranslationsRef.refetch();
    }
  }

  getVariantTranslations(): Observable<VariantTranslations[]> {
    this._getVariantTranslationsRef = this._apollo.watchQuery<GetVariantTranslations>({
      query: QUERIES.variantTranslations,
      variables: {},
      fetchPolicy: 'no-cache',
    });
    return this._getVariantTranslationsRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map(
        r =>
          r.data.variantTranslations.map(
            v => VariantTranslations.fromGql(v)
          )
      )
    );
  }

  getAllMaterials$(): Observable<{ materials: PortalModuleMaterial[] }> {
    this._getAllMaterialsRef = this._apollo.watchQuery<GetAllMaterials>({
      query: QUERIES.allMaterials,
      fetchPolicy: 'no-cache',
    });
    return this._getAllMaterialsRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data
          ? r.data.allMaterials
          : {
            edges: [],
            count: 0,
            pageInfo: {
              hasNextPage: false,
            },
          };
        return {
          materials: data.edges?.map(material => PortalModuleMaterial.fromGql(material.node)),
        };
      })
    );
  }

  getAllMaterialsRefresh(): void {
    this._getAllMaterialsRef.refetch();
  }

  getAllTextures$(): Observable<{ textures: PortalModuleTexture[] }> {
    this._getAllTexturesRef = this._apollo.watchQuery<GetAllTextures>({
      query: QUERIES.allTextures,
      fetchPolicy: 'no-cache',
    });
    return this._getAllTexturesRef.valueChanges.pipe(
      // Exclude partial but non-error results.
      filter(result => result.dataState === 'complete'),
      map((r) => {
        const data = r.data
          ? r.data.allTextures
          : {
            edges: [],
            count: 0,
            pageInfo: {
              hasNextPage: false,
            },
          };
        return {
          textures: data.edges?.map(texture => PortalModuleTexture.fromGql(texture.node)),
        };
      })
    );
  }

  getAllTexturesRefresh(): void {
    this._getAllTexturesRef.refetch();
  }

  /**
   * Mutations
   */

  /**
   * Upload a user
   */
  public uploadUser$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<UploadUser>({
      mutation: MUTATIONS.userMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.user?.uploadUser)
    );
  }

  /**
   * Performs a password reset mutation.
   */
  public passwordForgotten$(email: string): Observable<boolean> {
    return this._apollo.mutate<PasswordForgotten>({
      mutation: MUTATIONS.passwordForgotten,
      fetchPolicy: 'no-cache',
      variables: { email },
    }).pipe(map(result => result.data?.user?.passwordForgotten));
  }

  /**
   * Performs a MFA reset mutation. Note that on BE side it is checked if admin privileges are present.
   */
  public resetMfa$(userId: Id<User>): Observable<boolean> {
    return this._apollo.mutate<ResetMfaMutation>({
      mutation: MUTATIONS.resetMfaMutation,
      fetchPolicy: 'no-cache',
      variables: { userId },
    }).pipe(map(result => result.data?.user?.resetMfa));
  }

  /**
   * Upload a retailer
   */
  public newRetailer$(json: JSON): Observable<Retailer> {
    return this._apollo.mutate<AddRetailer>({
      mutation: MUTATIONS.addRetailerMutation,
      fetchPolicy: 'no-cache',
      variables: { ...json },
    }).pipe(
      map(result => Retailer.fromGql(result.data?.retailer.newRetailer))
    );
  }

  /**
   * Copy a retailer
   * @param srcId
   * @param json
   */
  public copyRetailer$(srcId: Id<Retailer>, json: JSON): Observable<Retailer> {
    return this._apollo.mutate<CopyRetailer>({
      mutation: MUTATIONS.copyRetailerMutation,
      fetchPolicy: 'no-cache',
      variables: { srcId, ...json },
    }).pipe(
      map(result => Retailer.fromGql(result.data?.retailer.copyRetailer))
    );
  }

  public updateRetailer$(id: Id<Retailer>, json: JSON): Observable<boolean> {
    return this._apollo.mutate<UpdateRetailer>({
      mutation: MUTATIONS.updateRetailerMutation,
      fetchPolicy: 'no-cache',
      variables: { id, ...json },
    }).pipe(
      map(result => !!result.data?.retailer.updateRetailer)
    );
  }

  /**
   * Update the catalog
   */
  public updateCatalog$(json: JSON, scopes: Id<Retailer>[]): Observable<boolean> {
    return this._apollo.mutate<UpdateCatalogMutation>({
      mutation: MUTATIONS.updateCatalogMutation,
      fetchPolicy: 'no-cache',
      variables: { scopes, ...json },
    }).pipe(
      map(result => !!result.data)
    );
  }

  /**
   * Updates a module category if an existing one is provided; else creates a new one with the given input
   */
  upsertModuleCategory$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<UpsertModuleCategoryMutation>({
      mutation: MUTATIONS.upsertModuleCategory,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.uploadModuleCategory)
    );
  }

  /**
   * Upload an array of non-wood price stocks
   */
  public uploadSinglePriceStocks$(json: JSON, scopes: string[]): Observable<boolean> {
    return this._apollo.mutate<NewSinglePriceStocks>({
      mutation: MUTATIONS.singlePriceStockMutation,
      fetchPolicy: 'no-cache',
      variables: { scopes, ...json },
    }).pipe(
      map(result => !!result.data?.retailer.newSinglePriceStocks)
    );
  }

  /**
   * Upload an array of wood price stocks
   */
  public uploadWoodPriceStocks$(json: JSON, scopes: string[]): Observable<boolean> {
    return this._apollo.mutate<NewWoodPriceStocks>({
      mutation: MUTATIONS.woodPriceStockMutation,
      fetchPolicy: 'no-cache',
      variables: { scopes, ...json },
    }).pipe(
      map(result => !!result.data?.retailer.newWoodPriceStocks)
    );
  }

  /**
   * Create a new blueprint (draft)
   */
  public newBlueprint$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<AddBlueprint>({
      mutation: MUTATIONS.addBlueprintMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.newBlueprint)
    );
  }

  /**
   * Update a blueprint
   */
  public updateBlueprint$(
    blueprintId: Id<PortalModuleBlueprint>,
    json: JSON,
    showAlert: boolean = true
  ): Observable<boolean> {
    const opt: ApolloClient.MutateOptions<UpdateBlueprint> = {
      mutation: MUTATIONS.updateBlueprintMutation,
      fetchPolicy: 'no-cache',
      variables: { id: blueprintId, ...json },
    };
    if (!showAlert) {
      opt.context = { noAlert: true };
    }

    return this._apollo.mutate<UpdateBlueprint>(opt).pipe(
      map(result => !!result.data?.module.updateBlueprint)
    );
  }

  /**
   * Copy a blueprint (to new draft)
   */
  public copyBlueprint$(blueprintId: Id<PortalModuleBlueprint>, json: JSON): Observable<boolean> {
    return this._apollo.mutate<CopyBlueprint>({
      mutation: MUTATIONS.copyBlueprintMutation,
      fetchPolicy: 'no-cache',
      variables: { id: blueprintId, ...json },
    }).pipe(
      map(result => !!result.data?.module.copyBlueprint)
    );
  }

  /**
   * Delete a blueprint
   */
  public deleteBlueprint$(blueprintId: Id<PortalModuleBlueprint>): Observable<boolean> {
    return this._apollo.mutate<DeleteBlueprint>({
      mutation: MUTATIONS.deleteBlueprintMutation,
      fetchPolicy: 'no-cache',
      variables: { blueprintId },
    }).pipe(
      map(result => !!result.data?.module.deleteBlueprint)
    );
  }

  /**
   * Archive a blueprint
   */
  public archiveBlueprint$(blueprintId: Id<PortalModuleBlueprint>): Observable<boolean> {
    return this._apollo.mutate<ArchiveBlueprint>({
      mutation: MUTATIONS.archiveBlueprintMutation,
      fetchPolicy: 'no-cache',
      variables: { blueprintId },
    }).pipe(
      map(result => !!result.data?.module.archiveBlueprint)
    );
  }

  /**
   * Publish a blueprint
   */
  public publishBlueprint$(blueprintId: Id<PortalModuleBlueprint>): Observable<boolean> {
    return this._apollo.mutate<PublishBlueprint>({
      mutation: MUTATIONS.publishBlueprintMutation,
      fetchPolicy: 'no-cache',
      variables: { blueprintId },
    }).pipe(
      map(result => !!result.data?.module.publishBlueprint)
    );
  }

  /**
   * Create a new event template
   */
  public newEventTemplate$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<NewEventTemplate>({
      mutation: MUTATIONS.newEventTemplate,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.newEventTemplate)
    );
  }

  /**
   * Update an event template
   */
  public updateEventTemplate$(
    eventTemplateId: Id<PortalEventTemplate>,
    json: JSON,
    showAlert: boolean = true
  ): Observable<boolean> {
    const opt: ApolloClient.MutateOptions<UpdateEventTemplate> = {
      mutation: MUTATIONS.updateEventTemplate,
      fetchPolicy: 'no-cache',
      variables: { id: eventTemplateId, ...json },
    };
    if (!showAlert) {
      opt.context = { noAlert: true };
    }

    return this._apollo.mutate<UpdateEventTemplate>(opt).pipe(
      map(result => !!result.data?.module.updateEventTemplate)
    );
  }

  /**
   * Create a new module invariant template
   */
  public newModuleInvariantTemplate$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<NewModuleInvariantTemplate>({
      mutation: MUTATIONS.newModuleInvariantTemplate,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.newInvariantTemplate)
    );
  }

  /**
   * Update an module invariant template
   */
  public updateModuleInvariantTemplate$(
    id: Id<PortalEventTemplate>,
    json: JSON,
    showAlert: boolean = true
  ): Observable<boolean> {
    const opt: ApolloClient.MutateOptions<UpdateModuleInvariantTemplate> = {
      mutation: MUTATIONS.updateModuleInvariantTemplate,
      fetchPolicy: 'no-cache',
      variables: { id, ...json },
    };
    if (!showAlert) {
      opt.context = { noAlert: true };
    }

    return this._apollo.mutate<UpdateModuleInvariantTemplate>(opt).pipe(
      map(result => !!result.data?.module.updateInvariantTemplate)
    );
  }

  /**
   * Create a new env invariant template
   */
  public newEnvInvariantTemplate$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<NewEnvInvariantTemplate>({
      mutation: MUTATIONS.newEnvInvariantTemplate,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.retailer.newInvariantTemplate)
    );
  }

  /**
   * Update an env invariant template
   */
  public updateEnvInvariantTemplate$(
    id: Id<PortalEventTemplate>,
    json: JSON,
    showAlert: boolean = true
  ): Observable<boolean> {
    const opt: ApolloClient.MutateOptions<UpdateEnvInvariantTemplate> = {
      mutation: MUTATIONS.updateEnvInvariantTemplate,
      fetchPolicy: 'no-cache',
      variables: { id, ...json },
    };
    if (!showAlert) {
      opt.context = { noAlert: true };
    }
    return this._apollo.mutate<UpdateEnvInvariantTemplate>(opt).pipe(
      map(result => !!result.data?.retailer.updateInvariantTemplate)
    );
  }

  /**
   * Block user
   */
  public blockUser$(userId: Id<User>, block: boolean): Observable<boolean> {
    return this._apollo.mutate<BlockUser>({
      mutation: MUTATIONS.blockUserMutation,
      fetchPolicy: 'no-cache',
      variables: { userId, block },
    }).pipe(
      map(result => !!result.data?.user.block)
    );
  }

  /**
   * Set user roles
   */
  public setRoles$(userId: Id<User>, roles: string[]): Observable<boolean> {
    return this._apollo.mutate<SetRoles>({
      mutation: MUTATIONS.setRolesMutation,
      fetchPolicy: 'no-cache',
      variables: { userId, roles },
    }).pipe(
      map(result => !!result.data?.user.setRoles)
    );
  }

  /**
   * Set user scopes
   */
  public setScopes$(userId: Id<User>, scopes: string[]): Observable<boolean> {
    return this._apollo.mutate<SetScopes>({
      mutation: MUTATIONS.setScopesMutation,
      fetchPolicy: 'no-cache',
      variables: { userId, scopes },
    }).pipe(
      map(result => !!result.data?.user.setScopes)
    );
  }

  /**
   * Upload an array of variants
   */
  public uploadVariants$(blueprintId: Id<PortalModuleBlueprint>, variants: JSON): Observable<boolean> {
    return this._apollo.mutate<UploadVariants>({
      mutation: MUTATIONS.variantsMutation,
      fetchPolicy: 'no-cache',
      variables: { blueprintId, ...variants },
    }).pipe(
      map(result => result.data?.module.uploadVariants === true)
    );
  }

  /**
   * Updates a configuration's pre-configuration status and caopies it to any given scopes
   */
  public updateConfiguration$(
    code: string,
    setToPreConfiguration: boolean,
    forcePriceRefresh: boolean = false, scopes: string[],
    newCode: string,
    name: string
  ): Observable<boolean> {
    return this._apollo.mutate<UpdateConfiguration>({
      mutation: MUTATIONS.updateConfigurationMutation,
      fetchPolicy: 'no-cache',
      variables: { code, setToPreConfiguration, forcePriceRefresh, scopes, newCode, name },
    }).pipe(
      map(result => !!result.data?.configuration.updateConfigurationPortal)
    );
  }

  /**
   * Add a new attribute
   */
  public newAttribute$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<NewAttribute>({
      mutation: MUTATIONS.newAttributeMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.newAttribute)
    );
  }

  /**
   * Update an existing attribute
   */
  public updateAttribute$(id: Id<Attribute>, json: JSON): Observable<boolean> {
    return this._apollo.mutate<UpdateAttribute>({
      mutation: MUTATIONS.updateAttributeMutation,
      fetchPolicy: 'no-cache',
      variables: { id, ...json },
    }).pipe(
      map(result => !!result.data?.module.updateAttribute)
    );
  }

  /**
   * Sets the full list of translations of various translation types
   */
  public setVariantRetailerTranslations$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<SetVariantRetailerTranslations>({
      mutation: MUTATIONS.setVariantRetailerTranslationsMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.translations.setVariantRetailerTranslations)
    );
  }

  public setAppRetailerTranslations$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<SetAppRetailerTranslations>({
      mutation: MUTATIONS.setAppRetailerTranslationsMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.translations.setAppRetailerTranslations)
    );
  }

  public setAppDefaultTranslations$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<SetAppDefaultTranslations>({
      mutation: MUTATIONS.setAppDefaultTranslationsMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.translations.setAppDefaultTranslations)
    );
  }

  public setInvariantRetailerTranslations$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<SetInvariantRetailerTranslations>({
      mutation: MUTATIONS.setInvariantRetailerTranslationsMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.translations.setInvariantRetailerTranslations)
    );
  }

  public setAttributeRetailerTranslations$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<SetAttributeRetailerTranslations>({
      mutation: MUTATIONS.setAttributeRetailerTranslationsMutation,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.translations.setAttributeRetailerTranslations)
    );
  }

  public getTranslationUpdatingStatus$(): Observable<boolean> {
    return this._apollo.query<GetTranslationUpdatingStatus>({
      query: QUERIES.translationUpdatingStatus,
      fetchPolicy: 'no-cache',
    }).pipe(
      map(result => result.data?.translationUpdatingStatus)
    );
  }

  /**
   * Material mutations (new, update, delete)
   */
  public newMaterial$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<NewMaterial>({
      mutation: MUTATIONS.newMaterial,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.newMaterial)
    );
  }

  public updateMaterial$(id: Id<PortalModuleMaterial>, json: JSON): Observable<boolean> {
    return this._apollo.mutate<UpdateMaterial>({
      mutation: MUTATIONS.updateMaterial,
      fetchPolicy: 'no-cache',
      variables: { id, ...json },
    }).pipe(
      map(result => !!result.data?.module.updateMaterial)
    );
  }

  public deleteMaterial$(materialId: Id<PortalModuleMaterial>): Observable<boolean> {
    return this._apollo.mutate<DeleteMaterial>({
      mutation: MUTATIONS.deleteMaterial,
      fetchPolicy: 'no-cache',
      variables: { id: materialId },
    }).pipe(
      map(result => !!result.data?.module.deleteMaterial)
    );
  }

  /**
   * Texture mutations (new, update, delete)
   */
  public newTexture$(json: JSON): Observable<boolean> {
    return this._apollo.mutate<NewTexture>({
      mutation: MUTATIONS.newTexture,
      fetchPolicy: 'no-cache',
      variables: json,
    }).pipe(
      map(result => !!result.data?.module.newTexture)
    );
  }

  public updateTexture$(id: Id<PortalModuleTexture>, json: JSON): Observable<boolean> {
    return this._apollo.mutate<UpdateTexture>({
      mutation: MUTATIONS.updateTexture,
      fetchPolicy: 'no-cache',
      variables: { id, ...json },
    }).pipe(
      map(result => !!result.data?.module.updateTexture)
    );
  }

  public deleteTexture$(materialId: Id<PortalModuleTexture>): Observable<boolean> {
    return this._apollo.mutate<DeleteTexture>({
      mutation: MUTATIONS.deleteTexture,
      fetchPolicy: 'no-cache',
      variables: { id: materialId },
    }).pipe(
      map(result => !!result.data?.module.deleteTexture)
    );
  }

  public makeConfigWithVariant$(variantId: Id<PortalModuleVariant>): Observable<string> {
    return this._apollo.mutate<MakeConfigWithVariant>({
      mutation: MUTATIONS.makeConfigurationWithVariant,
      fetchPolicy: 'no-cache',
      variables: { variantId: variantId },
    }).pipe(
      map(result => result.data?.configuration?.makeConfigWithVariant)
    );
  }

  public getManualPayload$(
    code: string,
    placementsInfo: Map<Id<ModulePlacement>,
      SpacialPosition[]>
  ): Observable<PortalManualConfiguration> {
    const parsedPlacementsInfo = PlacementInformation.convertToPlacementInfoInput(placementsInfo);
    return this._apollo.mutate<GenerateManualPayload>({
      mutation: MUTATIONS.getManualPayload,
      fetchPolicy: 'no-cache',
      variables: { code, placementsInfo: parsedPlacementsInfo },
    }).pipe(
      map(result => PortalManualConfiguration.fromGql(result.data?.manual?.generateManualPayload))
    );
  }

  public getManualPayloadWithKey$(
    code: string,
    placementsInfo: Map<Id<ModulePlacement>, SpacialPosition[]>,
    retailerKey: string
  ): Observable<PortalManualConfiguration> {
    const parsedPlacementsInfo = PlacementInformation.convertToPlacementInfoInput(placementsInfo);
    return this._apollo.mutate<GenerateManualPayloadWithKey>({
      mutation: MUTATIONS.getManualPayloadWithKey,
      fetchPolicy: 'no-cache',
      variables: { code, placementsInfo: parsedPlacementsInfo },
      context: {
        headers: new HttpHeaders({
          'X-API-KEY': retailerKey,
        }),
      },
    }).pipe(
      map(result => PortalManualConfiguration.fromGql(result.data?.manual?.generateManualPayloadWithKey))
    );
  }

  public getRetailerApiKey$(retailerId: Id<Retailer>): Observable<string> {
    return this._apollo.query<GetRetailerApiKey>({
      query: QUERIES.getRetailerApiKey,
      fetchPolicy: 'no-cache',
      variables: { retailerId },
    }).pipe(
      map(result => result.data?.retailerApiKey)
    );
  }

  public getFreeEANs$(): Observable<number> {
    return this._apollo.query<GetFreeEans>({
      query: QUERIES.getFreeEans,
      fetchPolicy: 'no-cache',
    }).pipe(map(
      r => r.data?.freeEANs
    ));
  }

  public uploadEANs$(eans: string[], retailerId: Id<Retailer>): Observable<boolean> {
    return this._apollo.mutate<uploadEANsForRetailer>(
      {
        mutation: MUTATIONS.uploadEANsForRetailer,
        fetchPolicy: 'no-cache',
        variables: { eans, id: retailerId },
      }
    ).pipe(
      map(result => !!result.data?.retailer.uploadEANsForRetailer)
    );
  }

  public getDocumentationBySubjectType$(subjectType: SubjectType): Observable<Documentation> {
    return this._apollo.query<GetDocumentationBySubjectType>({
      query: QUERIES.getDocumentationBySubjectType,
      fetchPolicy: 'no-cache',
      variables: { subjectType },
    }).pipe(
      map(result => Documentation.fromGql(result.data.documentationBySubjectType))
    );
  }

  public updateDocumentation$(documentation: Documentation): Observable<boolean> {
    return this._apollo.mutate<updateDocumentation>({
      mutation: MUTATIONS.updateDocumentation,
      fetchPolicy: 'no-cache',
      variables: { id: documentation.id, documentation },
    }).pipe(
      map(result => result.data.module.updateDocumentation)
    );
  }
}
